import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';

// ✅ Add this bypass flag at the top
const BYPASS_NOTIFICATIONS = true;

// Notification types
export type NotificationType = 'success' | 'error' | 'warning' | 'info';

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  duration?: number; // in milliseconds, 0 means persistent
  timestamp: string;
  read: boolean;
  actionLabel?: string;
  onAction?: () => void;
  dismissible?: boolean;
  data?: any; // Additional data for the notification
}

export interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  maxNotifications: number;
  defaultDuration: number;
}

export interface NotificationContextValue extends NotificationState {
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => string;
  removeNotification: (id: string) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  clearAll: () => void;
  clearOld: (olderThanMs: number) => void;
  // Convenience methods
  showSuccess: (title: string, message: string, options?: Partial<Notification>) => string;
  showError: (title: string, message: string, options?: Partial<Notification>) => string;
  showWarning: (title: string, message: string, options?: Partial<Notification>) => string;
  showInfo: (title: string, message: string, options?: Partial<Notification>) => string;
}

// Initial state
const initialState: NotificationState = {
  notifications: [],
  unreadCount: 0,
  maxNotifications: 50,
  defaultDuration: 5000, // 5 seconds
};

// Action types
type NotificationAction =
  | { type: 'ADD_NOTIFICATION'; payload: Notification }
  | { type: 'REMOVE_NOTIFICATION'; payload: string }
  | { type: 'MARK_AS_READ'; payload: string }
  | { type: 'MARK_ALL_AS_READ' }
  | { type: 'CLEAR_ALL' }
  | { type: 'CLEAR_OLD'; payload: number };

// Reducer
function notificationReducer(state: NotificationState, action: NotificationAction): NotificationState {
  switch (action.type) {
    case 'ADD_NOTIFICATION': {
      const newNotifications = [action.payload, ...state.notifications];
      
      // Limit notifications to max count
      const limitedNotifications = newNotifications.slice(0, state.maxNotifications);
      
      return {
        ...state,
        notifications: limitedNotifications,
        unreadCount: limitedNotifications.filter(n => !n.read).length,
      };
    }

    case 'REMOVE_NOTIFICATION': {
      const filteredNotifications = state.notifications.filter(n => n.id !== action.payload);
      
      return {
        ...state,
        notifications: filteredNotifications,
        unreadCount: filteredNotifications.filter(n => !n.read).length,
      };
    }

    case 'MARK_AS_READ': {
      const updatedNotifications = state.notifications.map(n =>
        n.id === action.payload ? { ...n, read: true } : n
      );
      
      return {
        ...state,
        notifications: updatedNotifications,
        unreadCount: updatedNotifications.filter(n => !n.read).length,
      };
    }

    case 'MARK_ALL_AS_READ': {
      const updatedNotifications = state.notifications.map(n => ({ ...n, read: true }));
      
      return {
        ...state,
        notifications: updatedNotifications,
        unreadCount: 0,
      };
    }

    case 'CLEAR_ALL': {
      return {
        ...state,
        notifications: [],
        unreadCount: 0,
      };
    }

    case 'CLEAR_OLD': {
      const cutoffTime = Date.now() - action.payload;
      const filteredNotifications = state.notifications.filter(n => 
        new Date(n.timestamp).getTime() > cutoffTime
      );
      
      return {
        ...state,
        notifications: filteredNotifications,
        unreadCount: filteredNotifications.filter(n => !n.read).length,
      };
    }

    default:
      return state;
  }
}

// Create context
const NotificationContext = createContext<NotificationContextValue | null>(null);

// ✅ Modified Provider with bypass
export function NotificationProvider({ children }: { children: ReactNode }) {
  // ✅ Call ALL hooks first (including useEffect hooks)
  const [state, dispatch] = useReducer(notificationReducer, initialState);

  // Auto-remove notifications after their duration
  useEffect(() => {
    if (BYPASS_NOTIFICATIONS) return; // Skip effect logic if bypassed
    
    const timers: Record<string, NodeJS.Timeout> = {};

    state.notifications.forEach(notification => {
      if (notification.duration && notification.duration > 0 && !timers[notification.id]) {
        timers[notification.id] = setTimeout(() => {
          dispatch({ type: 'REMOVE_NOTIFICATION', payload: notification.id });
          delete timers[notification.id];
        }, notification.duration);
      }
    });

    // Cleanup timers on unmount or when notifications change
    return () => {
      Object.values(timers).forEach(timer => clearTimeout(timer));
    };
  }, [state.notifications]);

  // Cleanup old notifications periodically
  useEffect(() => {
    if (BYPASS_NOTIFICATIONS) return; // Skip effect logic if bypassed
    
    const cleanupInterval = setInterval(() => {
      // Remove notifications older than 24 hours
      dispatch({ type: 'CLEAR_OLD', payload: 24 * 60 * 60 * 1000 });
    }, 60 * 60 * 1000); // Check every hour

    return () => clearInterval(cleanupInterval);
  }, []);

  // ✅ Then do bypass check after ALL hooks
  if (BYPASS_NOTIFICATIONS) {
    return <>{children}</>;
  }

  // Generate unique ID for notifications
  const generateId = (): string => {
    return `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  };

  // Add notification
  const addNotification = (notificationData: Omit<Notification, 'id' | 'timestamp' | 'read'>): string => {
    const id = generateId();
    const notification: Notification = {
      id,
      timestamp: new Date().toISOString(),
      read: false,
      duration: notificationData.duration ?? state.defaultDuration,
      dismissible: notificationData.dismissible ?? true,
      ...notificationData,
    };

    console.log(`🔔 NotificationContext: Adding ${notification.type} notification:`, notification.title);
    dispatch({ type: 'ADD_NOTIFICATION', payload: notification });
    
    return id;
  };

  // Remove notification
  const removeNotification = (id: string): void => {
    console.log(`🗑️ NotificationContext: Removing notification:`, id);
    dispatch({ type: 'REMOVE_NOTIFICATION', payload: id });
  };

  // Mark as read
  const markAsRead = (id: string): void => {
    console.log(`👁️ NotificationContext: Marking notification as read:`, id);
    dispatch({ type: 'MARK_AS_READ', payload: id });
  };

  // Mark all as read
  const markAllAsRead = (): void => {
    console.log(`👁️ NotificationContext: Marking all notifications as read`);
    dispatch({ type: 'MARK_ALL_AS_READ' });
  };

  // Clear all notifications
  const clearAll = (): void => {
    console.log(`🧹 NotificationContext: Clearing all notifications`);
    dispatch({ type: 'CLEAR_ALL' });
  };

  // Clear old notifications
  const clearOld = (olderThanMs: number): void => {
    console.log(`🧹 NotificationContext: Clearing notifications older than ${olderThanMs}ms`);
    dispatch({ type: 'CLEAR_OLD', payload: olderThanMs });
  };

  // Convenience methods for different notification types
  const showSuccess = (title: string, message: string, options: Partial<Notification> = {}): string => {
    return addNotification({
      type: 'success',
      title,
      message,
      ...options,
    });
  };

  const showError = (title: string, message: string, options: Partial<Notification> = {}): string => {
    return addNotification({
      type: 'error',
      title,
      message,
      duration: 0, // Errors are persistent by default
      ...options,
    });
  };

  const showWarning = (title: string, message: string, options: Partial<Notification> = {}): string => {
    return addNotification({
      type: 'warning',
      title,
      message,
      duration: 8000, // Warnings last longer
      ...options,
    });
  };

  const showInfo = (title: string, message: string, options: Partial<Notification> = {}): string => {
    return addNotification({
      type: 'info',
      title,
      message,
      ...options,
    });
  };

  // Context value
  const contextValue: NotificationContextValue = {
    ...state,
    addNotification,
    removeNotification,
    markAsRead,
    markAllAsRead,
    clearAll,
    clearOld,
    showSuccess,
    showError,
    showWarning,
    showInfo,
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  );
}

// ✅ Modified hook with bypass
export function useNotifications(): NotificationContextValue {
  // ✅ Call hooks first
  const context = useContext(NotificationContext);
  
  // ✅ Then do bypass check
  if (BYPASS_NOTIFICATIONS) {
    return {
      notifications: [],
      unreadCount: 0,
      maxNotifications: 50,
      defaultDuration: 5000,
      addNotification: () => 'mock-id',
      removeNotification: () => {},
      markAsRead: () => {},
      markAllAsRead: () => {},
      clearAll: () => {},
      clearOld: () => {},
      showSuccess: (title: string, message: string) => {
        console.log('✅ Success:', title, message);
        return 'mock-id';
      },
      showError: (title: string, message: string) => {
        console.log('❌ Error:', title, message);
        return 'mock-id';
      },
      showWarning: (title: string, message: string) => {
        console.log('⚠️ Warning:', title, message);
        return 'mock-id';
      },
      showInfo: (title: string, message: string) => {
        console.log('ℹ️ Info:', title, message);
        return 'mock-id';
      },
    };
  }
  
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  
  return context;
}

// Specialized hooks for specific use cases
export function useToast() {
  const { showSuccess, showError, showWarning, showInfo } = useNotifications();

  const toast = {
    success: (message: string, title: string = 'Success') => showSuccess(title, message),
    error: (message: string, title: string = 'Error') => showError(title, message),
    warning: (message: string, title: string = 'Warning') => showWarning(title, message),
    info: (message: string, title: string = 'Info') => showInfo(title, message),
  };

  return toast;
}

export function useNotificationActions() {
  const { addNotification, removeNotification, markAsRead, markAllAsRead, clearAll } = useNotifications();

  // Create notification with action button
  const createActionNotification = (
    type: NotificationType,
    title: string,
    message: string,
    actionLabel: string,
    onAction: () => void,
    options: Partial<Notification> = {}
  ): string => {
    return addNotification({
      type,
      title,
      message,
      actionLabel,
      onAction,
      duration: 0, // Action notifications are persistent
      ...options,
    });
  };

  // Common action notification patterns
  const confirmAction = (
    title: string,
    message: string,
    onConfirm: () => void,
    onCancel?: () => void
  ): string => {
    return createActionNotification(
      'warning',
      title,
      message,
      'Confirm',
      () => {
        onConfirm();
        // The notification will be removed automatically
      }
    );
  };

  const undoAction = (
    title: string,
    message: string,
    onUndo: () => void,
    duration: number = 10000
  ): string => {
    return addNotification({
      type: 'info',
      title,
      message,
      actionLabel: 'Undo',
      onAction: onUndo,
      duration,
    });
  };

  const retryAction = (
    title: string,
    message: string,
    onRetry: () => void
  ): string => {
    return createActionNotification(
      'error',
      title,
      message,
      'Retry',
      onRetry
    );
  };

  return {
    createActionNotification,
    confirmAction,
    undoAction,
    retryAction,
    removeNotification,
    markAsRead,
    markAllAsRead,
    clearAll,
  };
}

// Hook for notification statistics
export function useNotificationStats() {
  const { notifications, unreadCount } = useNotifications();

  const stats = {
    total: notifications.length,
    unread: unreadCount,
    read: notifications.length - unreadCount,
    byType: {
      success: notifications.filter(n => n.type === 'success').length,
      error: notifications.filter(n => n.type === 'error').length,
      warning: notifications.filter(n => n.type === 'warning').length,
      info: notifications.filter(n => n.type === 'info').length,
    },
    recent: notifications.filter(n => {
      const notificationTime = new Date(n.timestamp).getTime();
      const oneHourAgo = Date.now() - 60 * 60 * 1000;
      return notificationTime > oneHourAgo;
    }).length,
  };

  return stats;
}

// Hook for filtering and searching notifications
export function useNotificationFilters() {
  const { notifications } = useNotifications();

  const filterNotifications = (filters: {
    type?: NotificationType[];
    read?: boolean;
    search?: string;
    dateFrom?: Date;
    dateTo?: Date;
  }) => {
    return notifications.filter(notification => {
      // Type filter
      if (filters.type && filters.type.length > 0) {
        if (!filters.type.includes(notification.type)) return false;
      }

      // Read status filter
      if (filters.read !== undefined) {
        if (notification.read !== filters.read) return false;
      }

      // Search filter
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        const titleMatch = notification.title.toLowerCase().includes(searchTerm);
        const messageMatch = notification.message.toLowerCase().includes(searchTerm);
        if (!titleMatch && !messageMatch) return false;
      }

      // Date range filter
      const notificationDate = new Date(notification.timestamp);
      if (filters.dateFrom && notificationDate < filters.dateFrom) return false;
      if (filters.dateTo && notificationDate > filters.dateTo) return false;

      return true;
    });
  };

  const getNotificationsByType = (type: NotificationType) => {
    return notifications.filter(n => n.type === type);
  };

  const getUnreadNotifications = () => {
    return notifications.filter(n => !n.read);
  };

  const getRecentNotifications = (hours: number = 24) => {
    const cutoffTime = Date.now() - hours * 60 * 60 * 1000;
    return notifications.filter(n => 
      new Date(n.timestamp).getTime() > cutoffTime
    );
  };

  return {
    filterNotifications,
    getNotificationsByType,
    getUnreadNotifications,
    getRecentNotifications,
  };
}

export default NotificationContext;